import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import "./Services.css";

// Import your images
import img1 from "../../Images/kitchen.jpg";
import img2 from "../../Images/bathroom.jpg";
import img3 from "../../Images/home.jpg";
import img4 from "../../Images/painting.jpg";
import { getProperties, OrderProperty, VerifyPayment } from "../../Services/Operations/ProductAPI";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
const GOLD = "#e78610";
const TAN = "#bba58b";
const WHITE = "#ffffff";
const DARK = "#1a1208";

// ── Category config: image (Unsplash), icon, gradient ──
const CATEGORY_CONFIG = {
  kitchen: {
    img: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&q=80",
    icon: "🍳",
    label: "Kitchen",
    gradient: "linear-gradient(135deg, rgba(229,147,46,0.35) 0%, rgba(180,100,20,0.2) 100%)",
  },
  bathroom: {
    img: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=600&q=80",
    icon: "🚿",
    label: "Bathroom",
    gradient: "linear-gradient(135deg, rgba(100,180,220,0.3) 0%, rgba(60,130,180,0.15) 100%)",
  },
  full_home: {
    img: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600&q=80",
    icon: "🛋️",
    label: "Living Room",
    gradient: "linear-gradient(135deg, rgba(187,165,139,0.3) 0%, rgba(140,110,80,0.15) 100%)",
  },
  bedroom: {
    img: "https://images.unsplash.com/photo-1540518614846-7eded433c457?w=600&q=80",
    icon: "🛏️",
    label: "Bedroom",
    gradient: "linear-gradient(135deg, rgba(160,130,200,0.25) 0%, rgba(100,80,150,0.15) 100%)",
  },
  flooring: {
    img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80",
    icon: "🪵",
    label: "Flooring",
    gradient: "linear-gradient(135deg, rgba(160,110,60,0.3) 0%, rgba(120,80,40,0.15) 100%)",
  },
  office1: {
    img: "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=600&q=80",
    icon: "🏠",
    label: "Office",
    gradient: "linear-gradient(135deg, rgba(80,160,100,0.25) 0%, rgba(50,120,70,0.15) 100%)",
  },
  office: {
    img: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=600&q=80",
    icon: "🏛️",
    label: "Office",
    gradient: "linear-gradient(135deg, rgba(229,147,46,0.25) 0%, rgba(187,165,139,0.1) 100%)",
  },
};

const getCategoryConfig = (category) =>
  CATEGORY_CONFIG[category?.toLowerCase()] || CATEGORY_CONFIG.default;

const formatPrice = (price, type) => {
  const num = parseFloat(price).toLocaleString("en-IN", {
    style: "currency", currency: "INR", maximumFractionDigits: 0,
  });
  return type === "per_sqft" ? `${num} / sq.ft` : num;
};

const formatArea = (val) =>
  val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val;

const stripHtml = (html) => html?.replace(/<[^>]*>/g, "").trim() || "";

// ── Dummy API data ──
const dummyServices = [
  {
    id: 5,
    package_name: "Renovate Test Premium",
    category: "kitchen",
    description: "<p>Transform your kitchen into a culinary paradise with our premium renovation package. Includes custom cabinetry, granite countertops, and smart appliances.</p>",
    base_price: "5000.00",
    price_type: "fixed",
    duration_days: 12,
    min_area: 5000,
    max_area: 50000,
    material_details: "Premium hardwood cabinets, Italian granite countertops, German faucets",
    work_scope: "Full kitchen demolition, plumbing, electrical, tiling, carpentry and finishing",
    status: "active",
    created_at: "2026-04-09 08:40:07",
    updated_at: "2026-04-09 08:40:32",
  },
  {
    id: 6,
    package_name: "Luxe Bath Overhaul",
    category: "bathroom",
    description: "<p>A spa-like bathroom experience awaits. Full suite replacement, waterproofing, premium tiles, and designer fixtures included.</p>",
    base_price: "3500.00",
    price_type: "fixed",
    duration_days: 8,
    min_area: 2000,
    max_area: 15000,
    material_details: "Porcelain tiles, chrome fixtures, tempered glass partitions",
    work_scope: "Waterproofing, plumbing upgrade, tiling, vanity installation and electrical",
    status: "active",
    created_at: "2026-04-10 09:00:00",
    updated_at: "2026-04-10 09:30:00",
  },
  {
    id: 7,
    package_name: "Living Space Revamp",
    category: "living_room",
    description: "<p>Complete living room transformation with premium flooring, accent walls, false ceiling with ambient lighting and premium paint finishes.</p>",
    base_price: "4200.00",
    price_type: "fixed",
    duration_days: 10,
    min_area: 8000,
    max_area: 60000,
    material_details: "Engineered hardwood, textured wallpapers, LED cove lighting",
    work_scope: "Flooring, painting, false ceiling, electrical and carpentry works",
    status: "active",
    created_at: "2026-04-11 10:00:00",
    updated_at: "2026-04-11 10:30:00",
  },
]


const Services = [
  {
    id: 1,
    title: "Modular Kitchen",
    desc: "Custom designs and functional layouts to create the kitchen of your dreams, blending style with practicality.",
    img: img1,
  },
  {
    id: 2,
    title: "Bathroom Renovation",
    desc: "Transform your bathroom into a spa-like retreat with luxury fittings and smart space optimization.",
    img: img2,
  },
  {
    id: 3,
    title: "Full Home Makeover",
    desc: "A comprehensive, end-to-end transformation of your entire living space, curated to your lifestyle.",
    img: img3,
  },
  {
    id: 4,
    title: "Painting & Interiors",
    desc: "Expert color consultation and flawless finishing for a stunning interior refresh that reflects your personality.",
    img: img4,
  },
];

function ServiceCard({ service, index, onBook }) {
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);
  const cat = getCategoryConfig(service.category);
  const navigate=useNavigate()

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      style={{
        position: "relative",
        background: `linear-gradient(145deg, rgba(255,255,255,0.065) 0%, rgba(187,165,139,0.045) 100%)`,
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border: `1px solid ${hovered ? "rgba(229,147,46,0.35)" : "rgba(187,165,139,0.18)"}`,
        borderRadius: 22,
        overflow: "hidden",
        boxShadow: hovered
          ? `0 28px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(229,147,46,0.15), 0 8px 24px rgba(229,147,46,0.12)`
          : `0 16px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(229,147,46,0.06)`,
        transition: "border-color 0.35s, box-shadow 0.35s",
        cursor: "default",
      }}
    >
      {/* ── Image ── */}
      <div style={{ position: "relative", height: 200, overflow: "hidden" }}>
        <motion.img
          src={cat?.img||""}
          alt={cat?.label}
          animate={{ scale: hovered ? 1.07 : 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          onError={e => { e.target.style.display = "none"; }}
        />

        {/* Dark overlay */}
        <div style={{
          position: "absolute", inset: 0,
          background: `linear-gradient(180deg, rgba(10,7,3,0.15) 0%, rgba(10,7,3,0.65) 100%)`,
        }} />

        {/* Category badge */}
        <div style={{
          position: "absolute", top: 14, left: 14,
          display: "flex", alignItems: "center", gap: 6,
          background: "rgba(10,7,3,0.65)",
          backdropFilter: "blur(8px)",
          border: `1px solid rgba(229,147,46,0.3)`,
          borderRadius: 10, padding: "5px 12px",
        }}>
          <span style={{ fontSize: 13 }}>{cat?.icon}</span>
          <span style={{
            fontFamily: "'Josefin Sans', sans-serif",
            fontSize: 9, fontWeight: 600,
            color: TAN, letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}>{cat?.label}</span>
        </div>

        {/* Status badge */}
        {service.status === "active" && (
          <div style={{
            position: "absolute", top: 14, right: 14,
            display: "flex", alignItems: "center", gap: 5,
            background: "rgba(74,222,128,0.12)",
            border: "1px solid rgba(74,222,128,0.3)",
            borderRadius: 8, padding: "4px 10px",
          }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#4ade80", boxShadow: "0 0 6px rgba(74,222,128,0.8)" }} />
            <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 9, color: "#4ade80", letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600 }}>Active</span>
          </div>
        )}

        {/* Price on image */}
        <div style={{
          position: "absolute", bottom: 14, right: 14,
          textAlign: "right",
        }}>
          <div style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 24, fontWeight: 600, color: WHITE,
            lineHeight: 1, textShadow: "0 2px 12px rgba(0,0,0,0.6)",
          }}>
            {formatPrice(service?.base_price, service?.price_type)}
          </div>
          <div style={{
            fontFamily: "'Josefin Sans', sans-serif",
            fontSize: 9, color: "rgba(255,255,255,0.5)",
            letterSpacing: "0.12em", textTransform: "uppercase",
          }}>
            {service.price_type === "fixed" ? "Fixed Price" : "Per Sq.ft"}
          </div>
        </div>

        {/* Gold bottom accent line */}
        <motion.div
          animate={{ scaleX: hovered ? 1 : 0 }}
          transition={{ duration: 0.4 }}
          style={{
            position: "absolute", bottom: 0, left: 0, right: 0,
            height: 2,
            background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
            transformOrigin: "center",
          }}
        />
      </div>

      {/* ── Content ── */}
      <div style={{ padding: "22px 24px 20px" }}>

        {/* Package name */}
        <h3 style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 21, fontWeight: 600, color: WHITE,
          letterSpacing: "0.02em", lineHeight: 1.2,
          marginBottom: 8,
        }}>
          {service.package_name}
        </h3>

        {/* Description */}
        <p style={{
          fontFamily: "'Josefin Sans', sans-serif",
          fontSize: 12, fontWeight: 300,
          color: "rgba(187,165,139,0.7)",
          lineHeight: 1.7, letterSpacing: "0.03em",
          marginBottom: 16,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}>
          {stripHtml(service.description)}
        </p>

        {/* Key stats row */}
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
          gap: 8, marginBottom: 16,
        }}>
          {[
            { icon: "⏱", label: "Duration", value: `${service.duration_days} days` },
            { icon: "📐", label: "Min Area", value: `${formatArea(service.min_area)} sq.ft` },
            { icon: "📏", label: "Max Area", value: `${formatArea(service.max_area)} sq.ft` },
          ].map(stat => (
            <div key={stat.label} style={{
              background: "rgba(187,165,139,0.07)",
              border: "1px solid rgba(187,165,139,0.13)",
              borderRadius: 10, padding: "10px 8px",
              textAlign: "center",
            }}>
              <div style={{ fontSize: 14, marginBottom: 3 }}>{stat.icon}</div>
              <div style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 14, fontWeight: 600, color: WHITE,
                lineHeight: 1, marginBottom: 3,
              }}>{stat.value}</div>
              <div style={{
                fontFamily: "'Josefin Sans', sans-serif",
                fontSize: 8, color: "rgba(187,165,139,0.5)",
                letterSpacing: "0.14em", textTransform: "uppercase",
              }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Expandable details */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.35, ease: "easeInOut" }}
              style={{ overflow: "hidden" }}
            >
              <div style={{
                borderTop: "1px solid rgba(187,165,139,0.1)",
                paddingTop: 14, marginBottom: 14,
              }}>
                {[
                  { icon: "🪵", label: "Materials", value: service.material_details },
                  { icon: "🔧", label: "Work Scope", value: service.work_scope },
                ].map(item => (
                  <div key={item.label} style={{ marginBottom: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                      <span style={{ fontSize: 12 }}>{item.icon}</span>
                      <span style={{
                        fontFamily: "'Josefin Sans', sans-serif",
                        fontSize: 9, fontWeight: 600, color: GOLD,
                        letterSpacing: "0.16em", textTransform: "uppercase",
                      }}>{item.label}</span>
                    </div>
                    <p style={{
                      fontFamily: "'Josefin Sans', sans-serif",
                      fontSize: 11, fontWeight: 300,
                      color: "rgba(187,165,139,0.65)",
                      lineHeight: 1.65, letterSpacing: "0.03em",
                      paddingLeft: 18,
                    }}>{item.value}</p>
                  </div>
                ))}

                {/* ID & dates */}
                <div style={{
                  display: "flex", gap: 8, flexWrap: "wrap",
                  paddingTop: 8, borderTop: "1px solid rgba(187,165,139,0.08)",
                }}>
                  {[
                    { label: "ID", val: `#${service.id.toString().padStart(4,"0")}` },
                    { label: "Created", val: service.created_at.split(" ")[0] },
                    { label: "Updated", val: service.updated_at.split(" ")[0] },
                  ].map(m => (
                    <span key={m.label} style={{
                      fontFamily: "'Josefin Sans', sans-serif",
                      fontSize: 9, color: "rgba(187,165,139,0.4)",
                      letterSpacing: "0.1em",
                      background: "rgba(187,165,139,0.06)",
                      border: "1px solid rgba(187,165,139,0.1)",
                      borderRadius: 6, padding: "3px 8px",
                    }}>
                      {m.label}: {m.val}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Action row */}
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {/* Details toggle */}
          <button
            onClick={() => setExpanded(e => !e)}
            style={{
              flex: 1,
              padding: "11px 14px",
              background: "rgba(187,165,139,0.08)",
              border: "1px solid rgba(187,165,139,0.2)",
              borderRadius: 12,
              color: "rgba(255,255,255,0.65)",
              fontFamily: "'Josefin Sans', sans-serif",
              fontSize: 10, fontWeight: 600,
              letterSpacing: "0.14em", textTransform: "uppercase",
              cursor: "pointer", transition: "all 0.3s",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = "rgba(187,165,139,0.14)";
              e.currentTarget.style.borderColor = "rgba(187,165,139,0.35)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = "rgba(187,165,139,0.08)";
              e.currentTarget.style.borderColor = "rgba(187,165,139,0.2)";
            }}
          >
            {expanded ? "▲ Less" : "▼ Details"}
          </button>

          {/* Book button */}
          <motion.button
            whileHover={{ scale: 1.03, y: -1 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate('/service/'+service.id)}
            style={{
              flex: 2,
              padding: "11px 20px",
              background: `linear-gradient(135deg, #f0a84a 0%, ${GOLD} 50%, #c97d22 100%)`,
              border: "none",
              borderRadius: 12,
              color: DARK,
              fontFamily: "'Josefin Sans', sans-serif",
              fontSize: 11, fontWeight: 700,
              letterSpacing: "0.18em", textTransform: "uppercase",
              cursor: "pointer",
              boxShadow: `0 6px 20px rgba(229,147,46,0.35), inset 0 1px 0 rgba(255,255,255,0.25)`,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
          >
            <span>View</span>
            <span style={{ fontSize: 13 }}>→</span>
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
}

// ── Booking Modal ──


export default function ServicesGrid() {
  const navigate=useNavigate()
      const { isAuthenticated ,token} = useSelector((state) => state.auth);

   const [bookingService, setBookingService] = useState(null);

  const [services,setServices]=useState(dummyServices)
  const handleGetService=async()=>{
    try {
      const res= await getProperties()
      console.log(res);
      if(res.status===200){
        setServices(res.data.data)
      }
      
    } catch (error) {
      console.log(error);
      
    }
  }

  useEffect(() => {
    handleGetService()
  }, [])


      const handleOrder=async(id)=>{
        try { let data={property_id:id}
            const res=await OrderProperty(data,token)
            console.log(res);
            if(res.status===200){
                toast.success("Order Booked")
                handlePayment(res.data)
            }
          //  
            
        } catch (error) {
            console.log(error);
            
        }
    }

          const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const verifyPayment=async(data)=>{
try {
  if(!token){
     toast.warning("Unauthorized login first")
        navigate("/login")
  }
  console.log(data);
  const res= await VerifyPayment(data,token)
  console.log(res);
  if(res.status===200){
    toast.success(
"Payment successful & property booked")

setBookingService(null)
  }
  
  
  
} catch (error) {
  console.log(error);
  toast.error("Payment failed")
}
}


  const handlePayment = async (data) => {
    const res = await loadRazorpayScript();

    if (!res) {
      alert("Razorpay SDK failed to load");
      return;
    }

    // Call your backend to create order
    // const data = await fetch("/api/create-order", {
    //   method: "POST",
    // }).then((t) => t.json());

    const options = {
      key: data.key, // from Razorpay dashboard
      amount: data.amount,
      currency: "INR",
      name: "Siance Software",
      description: "Test Transaction",
      order_id: data.order_id,

      handler: function (response) {
        console.log("Payment Success:", response);
         verifyPayment(response)
        // send this response to backend for verification
        // response.razorpay_payment_id
        // response.razorpay_order_id
        // response.razorpay_signature
      },
 
      prefill: {
        name: "John Doe",
        email: "john@example.com",
        contact: "9999999999",
      },

      theme: {
        color: "#3399cc",
      },
    };

   

    const paymentObject = new window.Razorpay(options);
    paymentObject.open();
  };

  function BookingModal({ service, onClose }) {
  const cat = getCategoryConfig(service?.category);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 100,
        background: "rgba(10,7,3,0.85)",
        backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
    >
      <motion.div
        initial={{ scale: 0.88, y: 24 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 16 }}
        transition={{ duration: 0.45, ease: [0.22,1,0.36,1] }}
        onClick={e => e.stopPropagation()}
        style={{
          background: `linear-gradient(145deg, rgba(30,20,8,0.97) 0%, rgba(20,14,5,0.97) 100%)`,
          border: `1px solid rgba(229,147,46,0.3)`,
          borderRadius: 24,
          padding: "36px 36px 32px",
          maxWidth: 420, width: "100%",
          boxShadow: `0 40px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(229,147,46,0.1)`,
          position: "relative",
        }}
      >
        {/* Gold top line */}
        <div style={{
          position: "absolute", top: 0, left: "10%", width: "80%", height: 2,
          background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
          borderRadius: 2,
        }}/>

        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>{cat.icon}</div>
          <h2 style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 24, fontWeight: 600, color: WHITE,
            marginBottom: 6,
          }}>Confirm Booking</h2>
          <p style={{
            fontFamily: "'Josefin Sans', sans-serif",
            fontSize: 10, color: TAN,
            letterSpacing: "0.16em", textTransform: "uppercase",
          }}>{service.package_name}</p>
        </div>

        {[
          { label: "Category", value: cat.label },
          { label: "Price", value: formatPrice(service.base_price, service.price_type) },
          { label: "Duration", value: `${service.duration_days} days` },
          { label: "Area Range", value: `${formatArea(service.min_area)} – ${formatArea(service.max_area)} sq.ft` },
        ].map((r, i) => (
          <div key={r.label} style={{
            display: "flex", justifyContent: "space-between",
            padding: "11px 0",
            borderBottom: i < 3 ? "1px solid rgba(187,165,139,0.08)" : "none",
          }}>
            <span style={{ fontFamily:"'Josefin Sans', sans-serif", fontSize:10, color:"rgba(187,165,139,0.5)", letterSpacing:"0.12em", textTransform:"uppercase" }}>{r.label}</span>
            <span style={{ fontFamily:"'Josefin Sans', sans-serif", fontSize:12, fontWeight:600, color:WHITE }}>{r.value}</span>
          </div>
        ))}

        <div style={{ display:"flex", gap:10, marginTop:24 }}>
          <button onClick={onClose} style={{
            flex:1, padding:"12px", borderRadius:12,
            background:"rgba(187,165,139,0.08)",
            border:"1px solid rgba(187,165,139,0.2)",
            color:"rgba(255,255,255,0.6)",
            fontFamily:"'Josefin Sans', sans-serif",
            fontSize:10, fontWeight:600, letterSpacing:"0.14em",
            textTransform:"uppercase", cursor:"pointer",
          }}>Cancel</button>
          <motion.button
            whileHover={{ scale:1.03 }}
            whileTap={{ scale:0.97 }}
            style={{
              flex:2, padding:"12px", borderRadius:12,
              background:`linear-gradient(135deg, #f0a84a, ${GOLD}, #c97d22)`,
              border:"none", color:DARK,
              fontFamily:"'Josefin Sans', sans-serif",
              fontSize:11, fontWeight:700, letterSpacing:"0.18em",
              textTransform:"uppercase", cursor:"pointer",
              boxShadow:`0 6px 20px rgba(229,147,46,0.35)`,
            }}
            onClick={()=>{console.log(service);
            ;handleOrder(service.id)}}
          >
            Confirm Booking →
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}
  
  return (
    <div style={{
        minHeight: "100vh",
        background: `
          radial-gradient(ellipse at 70% 20%, rgba(240, 213, 180, 0.13) 0%, transparent 55%),
          radial-gradient(ellipse at 15% 85%, rgba(226, 194, 155, 0.15) 0%, transparent 50%),
          linear-gradient(135deg, #2c2823 0%, #504a42 35%, #50493d 70%, #494641 100%)
        `,
        padding: "40px 24px 60px",
        fontFamily: "'Cormorant Garamond', Georgia, serif",
        position: "relative",
      }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Josefin+Sans:wght@300;400;600&display=swap');
          * { box-sizing: border-box; margin:0; padding:0; }
        `}</style>
  
        {/* BG grid */}
        <svg style={{ position:"fixed", inset:0, width:"100%", height:"100%", opacity:0.05, pointerEvents:"none", zIndex:0 }} xmlns="http://www.w3.org/2000/svg">
          <defs><pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse"><path d="M 60 0 L 0 0 0 60" fill="none" stroke={TAN} strokeWidth="0.5"/></pattern></defs>
          <rect width="100%" height="100%" fill="url(#grid)"/>
        </svg>
  
        <div style={{ position:"relative", zIndex:1, maxWidth:1000, margin:"0 auto" }}>
          {/* Header */}
          <motion.div
            initial={{ opacity:0, y:-20 }}
            animate={{ opacity:1, y:0 }}
            transition={{ duration:0.6 }}
            style={{ textAlign:"center", marginBottom:48 }}
          >
           
            <h1 style={{
              fontFamily:"'Cormorant Garamond', serif",
              fontSize:42, fontWeight:600, color:WHITE,
              letterSpacing:"0.02em", marginBottom:10,
            }}>Our Services</h1>
            <div style={{ width:60, height:2, background:`linear-gradient(90deg, transparent, ${GOLD}, transparent)`, margin:"0 auto 12px" }}/>
       
          </motion.div>
  
          {/* Cards grid */}
          <div style={{
            display:"grid",
            gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))",
            gap:22,
          }}>
            {services.map((service, i) => (
              <ServiceCard
                key={service.id}
                service={service}
                index={i}
                onBook={setBookingService}
              />
            ))}
          </div>
        </div>
  
        {/* Booking modal */}
        <AnimatePresence>
          {bookingService && (
            <BookingModal
              service={bookingService}
              onClose={() => setBookingService(null)}
            />
          )}
        </AnimatePresence>
      </div>
  );
}
